import React from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { animalData } from '../../data/animals';
import { useCart } from '../../context/CartContext';

const fmt = (p) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: p % 1 === 0 ? 0 : 2 }).format(p);

export function PettingZooSection() {
  const { addItem } = useCart();
  const zoo = animalData.pettingZoo;

  return (
    <section data-testid="petting-zoo-section" className="py-24 md:py-32 px-6 md:px-12 bg-surface">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-gold mb-4">Curated, Clean & Charming</p>
          <h2 className="font-display text-4xl md:text-6xl text-white uppercase tracking-wide mb-3">
            Luxury Petting Zoo
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-xl">
            Our curated collection of approximately 15–20 mini farm animals, perfect for guests of all ages.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Image - smaller */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative aspect-[4/3] overflow-hidden max-w-md">
              <img src={zoo.image} alt="Luxury Petting Zoo" className="w-full h-full object-cover" />
              <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1.5 border border-white/10">
                <span className="text-gold text-sm font-medium">{fmt(zoo.price)}</span>
              </div>
            </div>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <p className="text-white/60 text-sm leading-relaxed">
              {zoo.description}
            </p>

            <button
              data-testid="add-petting-zoo"
              onClick={() => addItem({ ...zoo, hours: 1 })}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-background text-xs font-semibold uppercase tracking-wide hover:bg-gold-light transition-colors duration-300"
            >
              <Plus className="w-4 h-4" /> Add to Estimate — {fmt(zoo.price)}
            </button>

            {/* Pony Add-ons */}
            <div className="pt-4 border-t border-white/10">
              <h3 className="text-white font-medium text-sm mb-4">Optional Pony Add-On (2 Ponies)</h3>
              <div className="space-y-3">
                {animalData.pettingZooAddons.map((addon) => (
                  <div key={addon.id} className="p-4 border border-white/10 bg-background/30 flex justify-between items-center">
                    <span className="text-white text-sm">{addon.name}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-gold font-medium text-sm">{fmt(addon.price)}</span>
                      <button
                        onClick={() => addItem({ ...addon, hours: 1 })}
                        className="p-2 bg-gold text-background hover:bg-gold-light transition-colors"
                        data-testid={`add-${addon.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
